import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Filter, X, RefreshCw, AlertCircle } from 'lucide-react';
import { ENDPOINTS, withQuery } from '../shared/endpoints';
import { get } from '../shared/apiClient';
import { HttpError } from '../shared/httpTypes';

interface TaxRule {
  id: number;
  rule_uuid: string;
  category: string;
  rule_type: string;
  filing_status: string;
  age_condition: string;
  income_min?: number;
  income_max?: number;
  tax_rate?: number;
  base_tax?: number;
  standard_deduction?: number;
  description?: string;
  notes?: string;
  country: string;
  tax_year: number;
  source_document?: string;
  extraction_method: string;
  confidence_score?: number;
  is_validated: boolean;
  validation_notes?: string;
  created_at: string;
  updated_at: string;
}

interface RulesSummary {
  total_rules: number;
  categories: Record<string, number>;
  countries: Record<string, number>;
  years: number[];
  filing_statuses: Record<string, number>;
}

const RulesList: React.FC = () => {
  const navigate = useNavigate();
  const [rules, setRules] = useState<TaxRule[]>([]);
  const [summary, setSummary] = useState<RulesSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<number | ''>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedFilingStatus, setSelectedFilingStatus] = useState<string>('');

  const loadRules = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const queryParams: Record<string, string | number> = {};
      if (selectedCountry) queryParams.country = selectedCountry;
      if (selectedYear) queryParams.tax_year = selectedYear;
      if (selectedCategory) queryParams.category = selectedCategory;
      if (selectedFilingStatus) queryParams.filing_status = selectedFilingStatus;

      const url = withQuery(ENDPOINTS.rules.list, queryParams);
      const response = await get<TaxRule[]>(url);
      setRules(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.message);
      } else {
        setError('Failed to load tax rules');
      }
      console.error('Error loading rules:', err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCountry, selectedYear, selectedCategory, selectedFilingStatus]);

  const loadSummary = useCallback(async () => {
    try {
      const response = await get<RulesSummary>(ENDPOINTS.rules.summary);
      if (response.data) {
        // Normalize summary data to ensure all properties exist
        setSummary({
          total_rules: response.data.total_rules || 0,
          categories: response.data.categories || {},
          countries: response.data.countries || {},
          years: Array.isArray(response.data.years) ? response.data.years : [],
          filing_statuses: response.data.filing_statuses || {},
        });
      }
    } catch (err) {
      console.error('Error loading summary:', err);
      // Summary is optional, don't show error
      // Set default summary
      setSummary({
        total_rules: 0,
        categories: {},
        countries: {},
        years: [],
        filing_statuses: {},
      });
    }
  }, []);

  useEffect(() => {
    loadRules();
    loadSummary();
  }, [loadRules, loadSummary]);

  // Filter rules by search term
  const filteredRules = rules.filter(rule => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      rule.description?.toLowerCase().includes(search) ||
      rule.category.toLowerCase().includes(search) ||
      rule.rule_type.toLowerCase().includes(search) ||
      rule.country.toLowerCase().includes(search)
    );
  });

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCountry('');
    setSelectedYear('');
    setSelectedCategory('');
    setSelectedFilingStatus('');
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Dashboard</span>
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Tax Rules Database</h1>
            <p className="text-gray-600">
              View and manage tax rules extracted from tax documents
            </p>
          </div>
          <button
            onClick={loadRules}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-sm text-gray-600">Total Rules</p>
            <p className="text-2xl font-bold text-gray-900">{summary.total_rules || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-sm text-gray-600">Categories</p>
            <p className="text-2xl font-bold text-gray-900">
              {summary.categories ? Object.keys(summary.categories).length : 0}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-sm text-gray-600">Countries</p>
            <p className="text-2xl font-bold text-gray-900">
              {summary.countries ? Object.keys(summary.countries).length : 0}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-sm text-gray-600">Tax Years</p>
            <p className="text-2xl font-bold text-gray-900">
              {summary.years ? summary.years.length : 0}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search rules..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Country Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country
            </label>
            <select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Countries</option>
              {summary && summary.countries && Object.keys(summary.countries).map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
          </div>

          {/* Year Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tax Year
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : '')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Years</option>
              {summary && summary.years && Array.isArray(summary.years) && summary.years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Categories</option>
              {summary && summary.categories && Object.keys(summary.categories).map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Clear Filters */}
        {(selectedCountry || selectedYear || selectedCategory || selectedFilingStatus || searchTerm) && (
          <button
            onClick={clearFilters}
            className="mt-4 flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <X className="w-4 h-4" />
            <span>Clear Filters</span>
          </button>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Rules Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading tax rules...</p>
          </div>
        ) : filteredRules.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-600">No tax rules found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Country/Year
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Filing Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rate/Amount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRules.map((rule) => (
                  <tr key={rule.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                        {rule.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {rule.rule_type}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-md">
                      {rule.description || 'No description'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div>{rule.country}</div>
                      <div className="text-xs text-gray-500">{rule.tax_year}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {rule.filing_status || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {rule.tax_rate !== undefined && rule.tax_rate !== null && (
                        <div>{rule.tax_rate}%</div>
                      )}
                      {rule.standard_deduction !== undefined && rule.standard_deduction !== null && (
                        <div className="text-xs text-gray-500">
                          Deduction: {rule.standard_deduction.toLocaleString()}
                        </div>
                      )}
                      {rule.base_tax !== undefined && rule.base_tax !== null && (
                        <div className="text-xs text-gray-500">
                          Base: {rule.base_tax.toLocaleString()}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Results Count */}
        {!isLoading && filteredRules.length > 0 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Showing {filteredRules.length} of {rules.length} rules
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RulesList;

